/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import MainPage from '../../MainPage';
import {
  Actions,
  DateFormat,
  PhotoResolution,
  sessionType,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  SocialIcon,
  CallIcon,
  TaskIcon,
  ScheduleAddPlusIcon,
  ScheduleListNewIcon,
  ErrorIcon,
  NewFilterIcon,
} from '../Common/Icons';
import { getDateFormat, getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import {
  getProfilePicUrl,
  getUsername,
  isSocial,
  isCare,
  isManage,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { getContacts } from './Contacts';
import { HSCard, HSCardSkeleton, SingleDatePicker } from '@/Components/Common/HSWidget';
import { UserImage } from './UserImage';
import { getProfilePics, getUserHubs } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import {
  Access,
  KnownActionsList,
  checkRoleAccess,
  isActionAllowed,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import useStartVideoCall from '@/Components/Hooks/useStartVideoCall';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TaskListItem from '../Task/TaskListItem';
import VisitIcon from '@/Components/Common/Icons/visit.svg';
import ScheduleCalendar from '../ScheduleCalendar/ScheduleCalendar';

dayjs.extend(utc);
dayjs.extend(timezone);

const getFloatingDayKey = (value) => {
  if (!value) return '';
  const dateValue = value instanceof Date ? value : new Date(value);
  return `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`;
};

const toFloatingDayDate = (value) => {
  if (!value) return value;
  if (dayjs.isDayjs(value)) {
    return new Date(value.year(), value.month(), value.date(), 12, 0, 0, 0);
  }

  const dateValue = value instanceof Date ? value : new Date(value);
  return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 12, 0, 0, 0);
};

const SHOW_STATUS_BEFORE_MINUTES = 15;

/**
 * Converts a time-of-day string (HH:mm:ss) to total seconds.
 * Returns null if the input is invalid or cannot be parsed.
 */
const toSeconds = (time) => {
  if (!time) return null;
  const parts = String(time).split(':').map(Number);
  if (parts.some((p) => Number.isNaN(p))) return null;
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
};

const currentSiteIconZip = (state) => state.data.siteIconFiles;
const currentSiteIconManifest = (state) => state.data.siteIconManifest;

const ListSchedule = (props) => {
  const {
    onlyContent,
    parentBread,
    selectedDate,
    setSelectedDate,
    scheduleList,
    fetchingScheduleList,
    onActionClick,
    contacts,
    userInfo,
    userHubsData,
    hubRecord,
    openSubMenu = false,
    loginUserTimeformat,
    setParentSelectedTab,
    onCalendarChange,
  } = props;
  const trans = useSelector(translator);
  const dispatch = useDispatch();
  const { startCall } = useStartVideoCall();
  const resourcesMap = useSelector(getProfilePics());
  const header = parentBread || [];
  const currentUser = useSelector(getCurrentUser);
  const useruuid = useMemo(() => currentUser?.useruuid, [currentUser]);
  const permissions = useSelector(getPermissions());
  const currentUserData = useSelector((user) => user.data.userInfo);
  const currentUserDataInfo = currentUserData[openSubMenu ? hubRecord?.useruuid : useruuid];
  const userTimezone = getTimeZoneFormat(currentUserDataInfo.timezone);
  const userHubs = useSelector(getUserHubs(useruuid));
  const userUuid = hubRecord?.useruuid ?? currentUser?.useruuid;
  const newRecord = userHubs.find((a) => a.useruuid === userUuid);
  const record = hubRecord?.rolename ? hubRecord : newRecord;
  const isCareOrManageRole = isCare(record) || isManage(record);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const siteIconZip = useSelector(currentSiteIconZip);
  const siteIconManifest = useSelector(currentSiteIconManifest);
  const [originalFilteredData, setOriginalFilteredData] = useState({
    data: [],
    count: 0,
  });

  const handleFilterChange = (action, filterKey, closeDropdown) => {
    if (action === 'reset') {
      setFilteredEventListonTab(originalFilteredData); // Reset to the original data for the selected tab
      closeDropdown();
    } else if (action === 'cancel') {
      closeDropdown();
    } else if (action === 'apply') {
      filterItemsOnTabList(filterKey);
      closeDropdown();
    }
  };

  const filterItemsOnTabList = (filters) => {
    const statusFilters = filters.filter((filter) => filter?.groupKey === 'status');
    const recurrenceFilters = filters.filter((filter) => filter?.groupKey === 'recurrence');

    // Check if "non_recc_all" or "recc_all" is selected
    const isStatusAllSelected = statusFilters.some((filter) => filter?.itemKey === 'non_recc_all');
    const isRecurrenceAllSelected = recurrenceFilters.some(
      (filter) => filter?.itemKey === 'recc_all'
    );

    // If "non_recc_all" or "recc_all" is selected, return the original list
    if (isStatusAllSelected || isRecurrenceAllSelected) {
      setFilteredEventListonTab(originalFilteredData);
      return;
    }

    // Apply filters normally if "non_recc_all" or "recc_all" is not selected
    const filteredListData = originalFilteredData?.data?.map((group) =>
      sortEventsByTypePriority(
        group.filter((item) => {
          const statusMatch =
            statusFilters.length === 0 ||
            statusFilters.some((filter) => {
              if (item?.event_type === 'tasks') {
                return item?.task_status === filter?.itemKey;
              } else {
                return item?.event_status === filter?.itemKey;
              }
            });

          const recurrenceMatch =
            recurrenceFilters.length === 0 ||
            recurrenceFilters.some((filter) => {
              if (filter?.itemKey === 'recc_onetime') {
                return !item?.repeat;
              }
              return item?.repeat === filter?.itemKey;
            });

          // Both status and recurrence must match
          return statusMatch && recurrenceMatch;
        })
      )
    );

    const totalFilteredCount = filteredListData.flat().length;

    setFilteredEventListonTab({
      data: filteredListData,
      count: totalFilteredCount,
    });
  };

  const items = useMemo(() => {
    const tabItems = [
      { key: 'all', label: trans('ALL') },
      { key: 'call', label: trans('CALLS') },
      { key: 'reminder', label: trans('REMINDER') },
    ];

    // Add "Tasks" Tab only if user is Manage/Care
    if (isCareOrManageRole) {
      tabItems.push({ key: 'task', label: trans('TASKS') });
    }

    return tabItems;
  }, [isCareOrManageRole]);

  const [selectedTab] = useState('all');
  const selectedTabRef = useRef(selectedTab);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const selectedEventTypesRef = useRef(selectedEventTypes);
  useEffect(() => {
    selectedEventTypesRef.current = selectedEventTypes;
  }, [selectedEventTypes]);

  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    if (setParentSelectedTab) setParentSelectedTab(selectedTab);
  }, [selectedTab, setParentSelectedTab]);

  const getRelativeTime = (eventTimestamp, timeZone) => {
    const now = timeZone ? dayjs().tz(timeZone) : dayjs();
    const eventTime = timeZone ? dayjs(eventTimestamp).tz(timeZone) : dayjs(eventTimestamp);

    const diffInMinutes = now.diff(eventTime, 'minute');

    if (diffInMinutes < 0) return null; // Event is in the future
    if (diffInMinutes < 60)
      return `${diffInMinutes} ${trans('MIN_L')}${diffInMinutes > 1 ? trans('S_L') : ''} ${trans('BEFORE')}`;

    const diffInHours = now.diff(eventTime, 'hour');
    if (diffInHours < 24) return `${diffInHours} ${trans('HR_U_BEFORE')}`;

    return null; // Older than 24 hours
  };

  const allowAddEvent = useMemo(
    () =>
      (record?.rolename === undefined ||
        isActionAllowed(record?.rolename, KnownActionsList.create_events, permissions)) &&
      hubRecord,
    [record, permissions, hubRecord]
  );

  const allowAddTask = useMemo(
    () => checkRoleAccess(Access.beta.labsPreview) && hubRecord,
    [hubRecord]
  );

  const dropdown = [
    !fetchingScheduleList &&
      allowAddEvent && {
        label: <div onClick={onActionClick(Actions.addEvent, userInfo)}>{trans('ADDCALL')}</div>,
        key: 'addevent',
      },
    !fetchingScheduleList &&
      allowAddEvent && {
        label: (
          <div onClick={onActionClick(Actions.addReminderEvent, userInfo)}>
            {trans('ADDREMINDER')}
          </div>
        ),
        key: 'editevent',
      },
    // Add "Add Task" only for Manage/Care roles & Non-Social Hubs
    isCareOrManageRole && {
      label: <div onClick={onActionClick(Actions.addTask, userInfo)}>{trans('ADDTASK')}</div>,
      key: 'addtask',
    },
  ].filter(Boolean);

  const scheduleAddDropdownProps = useMemo(
    () => ({
      menu: {
        items: dropdown,
      },
      trigger: ['click'],
      placement: 'bottomRight',
      overlayClassName: 'event_dropdown',
      overlayStyle: { minWidth: '120px' },
    }),
    [dropdown]
  );

  const formatDateToDisplayEventDate = (date) => {
    if (!date) return '';
    const d = typeof date.format === 'function' ? date : dayjs(date);
    return d.format('YYYY-MM-DD');
  };

  const getEventTypePriority = (eventType) => {
    // Priority order: call -> reminder -> visit -> task
    switch (eventType) {
      case 'schedule':
      case 'zoom':
        return 1; // calls
      case 'reminder':
        return 2; // reminders
      case 'home_visit':
        return 3; // visits
      case 'tasks':
        return 4; // tasks
      default:
        return 5;
    }
  };

  const sortEventsByTypePriority = (events) => {
    return [...events].sort((a, b) => {
      // Sort by event type priority FIRST
      const priorityDiff = getEventTypePriority(a.event_type) - getEventTypePriority(b.event_type);
      if (priorityDiff !== 0) return priorityDiff;
      // If same priority, then sort by timestamp (as numeric milliseconds)
      return new Date(a.event_timestamp).getTime() - new Date(b.event_timestamp).getTime();
    });
  };

  const formatList = useCallback(
    (scheduleList) => {
      if (!selectedDate) return [];
      const targetDateKey = getFloatingDayKey(selectedDate);

      let filteredList = scheduleList.filter((event) => {
        if (!event.event_timestamp) return false;
        const eventDateKey = dayjs(event.event_timestamp).tz(userTimezone).format('YYYY-MM-DD');
        return eventDateKey === targetDateKey;
      });

      const list = filteredList.reduce((acc, event) => {
        const dateObj = getDateFormat(event.event_timestamp, userTimezone);
        const date = dateObj.date;

        if (!acc[date]) {
          acc[date] = [];
        }

        const duplicate = acc[date].some((e) => e.schedule_eventuuid === event.schedule_eventuuid);

        if (!duplicate) {
          acc[date].push(event);
        }

        return acc;
      }, {});

      return Object.values(list)
        .map((group) => sortEventsByTypePriority(group))
        .sort((a, b) => {
          const minTimeA = Math.min(...a.map((event) => new Date(event.event_timestamp).getTime()));
          const minTimeB = Math.min(...b.map((event) => new Date(event.event_timestamp).getTime()));
          return minTimeA - minTimeB;
        });
    },
    [selectedDate, userTimezone]
  );

  const formattedList = useMemo(() => formatList(scheduleList), [scheduleList, formatList]);

  const [filteredEventListonTab, setFilteredEventListonTab] = useState({
    data: [],
    count: 0,
  });

  const eventMatchesTypes = useCallback((event, types) => {
    if (!types || types.length === 0) return true;
    return types.some((type) => {
      if (type === 'call') {
        return event?.event_type === 'schedule' || event?.event_type === 'zoom';
      }
      if (type === 'task') return event?.event_type === 'tasks';
      if (type === 'reminder') {
        return ['reminder', 'home_visit'].includes(event?.event_type);
      }
      return false;
    });
  }, []);

  const filteredListonTab = (types) => {
    let filteredListData;

    if (Array.isArray(types) && types.length > 0) {
      filteredListData = formattedList.map((group) =>
        sortEventsByTypePriority(group.filter((item) => eventMatchesTypes(item, types)))
      );
    } else {
      filteredListData = formattedList.map((group) =>
        sortEventsByTypePriority(
          isSocial(hubRecord) || !isCareOrManageRole
            ? group.filter((item) => item?.event_type !== 'tasks') // Exclude tasks for social and no role
            : group
        )
      );
    }
    const totalFilteredCount = filteredListData.flat().length;

    setOriginalFilteredData({
      data: filteredListData,
      count: totalFilteredCount,
    });

    setFilteredEventListonTab({
      data: filteredListData,
      count: totalFilteredCount,
    });
  };

  useEffect(() => {
    async function buildCategoryOptions() {
      if (!siteIconZip || !siteIconManifest || typeof siteIconZip.file !== 'function') {
        console.error('Invalid Zip file.');
        return;
      }
      const options = [];
      for (const mainCat of Object.keys(siteIconManifest)) {
        const subcats = siteIconManifest[mainCat];
        for (const subcat of Object.keys(subcats)) {
          const fileName = subcats[subcat];
          const file = siteIconZip.file(fileName);
          if (!file) {
            console.error(`File ${fileName} not found in zip for subcategory "${subcat}"`);
            continue;
          }
          try {
            const blob = await file.async('blob');
            let blobToUse = blob;
            if (!blob.type || blob.type === '') {
              blobToUse = new Blob([blob], { type: 'image/svg+xml' });
            }
            const url = URL.createObjectURL(blobToUse);
            options.push({
              value: `${subcat}`,
              icon: (
                <img
                  src={url}
                  alt={subcat}
                  style={{
                    width: 30,
                    height: 36,
                    verticalAlign: 'middle',
                  }}
                />
              ),
            });
          } catch (error) {
            console.error(`Error loading file ${fileName} for subcategory "${subcat}":`, error);
          }
        }
      }
      options.sort((a, b) => a.value.localeCompare(b.value));
      setCategoryOptions(options);
    }
    buildCategoryOptions();
  }, [siteIconZip, siteIconManifest]);

  useEffect(() => {
    if (formattedList) {
      filteredListonTab(selectedEventTypes);
    }
  }, [selectedEventTypes, formattedList, selectedDate, isCareOrManageRole, hubRecord]);

  const renderEventType = (eventType) => {
    switch (eventType) {
      case 'schedule':
      case 'zoom':
        return (
          <div className="event-type">
            <span style={{ justifyContent: 'center', display: 'flex' }}>
              <CallIcon className="event-type-icon event-icon-color" />{' '}
            </span>
            <span>{trans('CALL')}</span>
          </div>
        );
      case 'tasks':
        return (
          isCareOrManageRole && (
            <div className="event-type">
              <TaskIcon className="event-type-icon" />
              <span>{trans('TASK')}</span>
            </div>
          )
        );
      case 'reminder':
        return (
          <div className="event-type">
            <SocialIcon className="event-type-icon event-icon-color" /> {trans('REMINDER')}
          </div>
        );
      case 'home_visit':
        return (
          <div className="event-type">
            <div className="visit-icon-container">
              <VisitIcon className="visit-icon" />
            </div>
            {trans('VISIT')}
          </div>
        );
      default:
        return null;
    }
  };

  const contactImage = useCallback(
    (contact) => {
      let displayName = getUsername(contact);
      const url = getProfilePicUrl(contact.useruuid, resourcesMap, PhotoResolution.R64);
      return <UserImage name={displayName} url={url} />;
    },
    [resourcesMap]
  );

  const renderDateListItem = useCallback(
    (item) => {
      let dateObj = getDateFormat(item?.event_timestamp, userTimezone);
      return (
        <div
          key={dateObj.dayDateformat}
          className={
            dateObj.dayDateformat.startsWith('Today')
              ? 'schedule-list-date-newui schedule-list-today-newui'
              : 'schedule-list-date-newui'
          }
        >
          <div className="schedule-date-newui">{dateObj.dayDateformat}</div>
        </div>
      );
    },
    [userTimezone]
  );

  const renderInvitedList = useCallback(
    (invitedUseruuid) => {
      let invitedContacts = getContacts(invitedUseruuid, [
        ...(contacts ?? []),
        ...(userHubsData ?? []),
        ...(userInfo ? [userInfo] : []),
      ]);

      const count = invitedContacts.length;

      const invitedContactsData = invitedContacts.slice(0, 3).map((contact, index) => (
        <div key={index} className="user-image-container">
          {contact && contactImage(contact)}
        </div>
      ));
      return { invitedContactsData, count };
    },
    [contacts, userHubsData, userInfo, contactImage]
  );

  const handleButtonClick = useCallback(
    (item) => {
      if (item?.event_type?.toLowerCase() === sessionType.zoom) {
        window.open(item?.meeting_url);
      } else {
        startCall({
          meeting_id: item?.schedule_eventuuid,
          eventInfo: item,
        });
      }
    },
    [startCall]
  );

  const DaysOfWeekRow = (daysOfWeek, item) => {
    return (
      <div className="days_of_week">
        {['schedule', 'zoom', 'reminder', 'tasks'].includes(item?.event_type) ? (
          item?.repeat === 'daily' ? (
            <div className="day_info">{trans(String(item?.repeat).toUpperCase())}</div>
          ) : item?.repeat === 'weekly' ? (
            daysOfWeek?.map((day, index) => (
              <div key={index} className="day_info">
                {trans(String(day).slice(0, 3).toUpperCase())}
              </div>
            ))
          ) : item?.repeat === 'custom' ? (
            daysOfWeek?.map((day, index) => (
              <div key={index} className="day_info">
                {trans(String(day).slice(0, 3).toUpperCase())}
              </div>
            ))
          ) : (
            <div className="day_info">{trans('DOESNOTREPEAT')}</div>
          )
        ) : (
          daysOfWeek?.map((day, index) => (
            <div key={index} className="day_info">
              {trans(String(day).slice(0, 3).toUpperCase())}
            </div>
          ))
        )}
      </div>
    );
  };

  const translateDay = (word) => {
    return trans(word.toUpperCase());
  };

  const EventDaysInfo = (daysOfWeek, item) => {
    if (!daysOfWeek || daysOfWeek.length === 0) return '';
    const capitalizedDays = daysOfWeek.map(translateDay);

    if (capitalizedDays.length === 1) return `${trans('REPEATS')} ${capitalizedDays[0]}`;
    if (capitalizedDays.length === 2)
      return `${trans('REPEATS')} ${capitalizedDays[0]} ${trans('AND_L')} ${capitalizedDays[1]}`;

    const lastDay = capitalizedDays[capitalizedDays.length - 1];
    const otherDays = capitalizedDays.slice(0, -1).join(', ');
    return `${trans('REPEATS')} ${otherDays} ${trans('AND_L')} ${lastDay}`;
  };

  const renderButtonStatus = useCallback(
    (item) => {
      if (
        item?.event_type?.toLowerCase() === 'reminder' ||
        item?.event_type?.toLowerCase() === 'home_visit'
      ) {
        return null;
      }
      if (!item?.invited_contacts.includes(useruuid)) {
        return null;
      }

      const now = dayjs().tz(userTimezone);
      const eventTime = dayjs(item?.event_timestamp).tz(userTimezone);
      const diff = eventTime.diff(now, 'minute');

      // Compute the event end moment from event_starttime / event_endtime (time-of-day
      // strings in the principal user's timezone), falling back to duration_minutes,
      // then to a default 60-minute duration if no end info is available.
      const startSec = toSeconds(item?.event_starttime);
      const endSec = toSeconds(item?.event_endtime);
      let endMoment = null;
      if (startSec !== null && endSec !== null) {
        endMoment = eventTime.add(endSec - startSec, 'seconds');
      } else if (item?.duration_minutes) {
        endMoment = eventTime.add(Number(item.duration_minutes), 'minutes');
      } else {
        // Fallback: assume 60 minutes if no end time info is provided
        endMoment = eventTime.add(60, 'minutes');
      }
      // Show the join button only while the event is still ongoing (now <= end time).
      const isBeforeEnd = now.isBefore(endMoment) || now.isSame(endMoment);

      if (diff > 5 && diff <= SHOW_STATUS_BEFORE_MINUTES) {
        return (
          <div className="status" onClick={() => handleButtonClick(item)}>
            {trans('ABOUT_TO_L_START')}
          </div>
        );
      } else if (diff <= 5 && isBeforeEnd) {
        return (
          <div className="join_btn" onClick={() => handleButtonClick(item)}>
            <div className="join-call-classname">
              <span className="join-call-icon-classname">
                <CallIcon className="join-call-icon" />
              </span>
              <span className="join-call-label">{trans('JOINCALL')}</span>
            </div>
          </div>
        );
      } else {
        return null;
      }
    },
    [handleButtonClick, useruuid, userTimezone]
  );

  const renderNoList = useCallback(() => {
    let currentDate = getDateFormat(dayjs().tz(userTimezone).toISOString(), userTimezone);
    return (
      <div
        className={
          currentDate.dayDateformat.startsWith('Today')
            ? 'schedule-list-date-newui schedule-list-today-newui'
            : 'schedule-list-date-newui'
        }
      >
        <HSCard
          className="no-events-container"
          content={
            <div className="no-events-card">
              <div className="no-events-column">
                <ErrorIcon
                  className="error-icon"
                  fillColor="#3FA9F5"
                  style={{ width: '24px', height: '24px' }}
                />
                <div className="no-events-information-content">{trans('INFORMATION')}</div>
              </div>
              <div className="no-events-column">
                <div className="no-events-description-content">{trans('NOUPCOMINGEVENTS')}</div>
              </div>
            </div>
          }
        />
      </div>
    );
  }, [formattedList, userTimezone, trans]);

  const getEventIcon = useCallback(
    (event) => {
      switch (event.event_type) {
        case 'schedule':
        case 'zoom':
          return <CallIcon />;
        case 'reminder':
          return <SocialIcon />;
        case 'home_visit':
          return <VisitIcon />;
        case 'tasks': {
          const opt = categoryOptions.find((o) => o.value === event.category);
          if (opt) {
            return React.cloneElement(opt.icon, { style: { display: 'block' } });
          }
          return <TaskIcon />;
        }
        default:
          return <CallIcon />;
      }
    },
    [categoryOptions]
  );

  const getCalendarDateInUserTimezone = useCallback(
    (timestamp) => {
      if (!timestamp) return null;
      const zoned = userTimezone ? dayjs.utc(timestamp).tz(userTimezone) : dayjs(timestamp);
      if (!zoned.isValid()) return null;

      // Build a local Date with the wall-clock values from the user's timezone.
      return new Date(
        zoned.year(),
        zoned.month(),
        zoned.date(),
        zoned.hour(),
        zoned.minute(),
        zoned.second(),
        zoned.millisecond()
      );
    },
    [userTimezone]
  );

  const transformedEvents = useMemo(() => {
    return scheduleList
      .filter((event) => eventMatchesTypes(event, selectedEventTypes))
      .map((event) => {
        const start = getCalendarDateInUserTimezone(event.event_timestamp);
        if (!start) return null;

        const startSec = toSeconds(event?.event_starttime);
        const endSec = toSeconds(event?.event_endtime);
        const derivedDurationMinutes =
          startSec !== null && endSec !== null && endSec > startSec
            ? Math.round((endSec - startSec) / 60)
            : Number(event?.duration_minutes) > 0
              ? Number(event.duration_minutes)
              : 30;
        const end = new Date(start.getTime() + derivedDurationMinutes * 60 * 1000);

        return {
          id: event.schedule_eventuuid,
          title: event.event_name,
          iconEl: getEventIcon(event),
          eventType: event.event_type,
          start,
          end,
          _raw: event,
        };
      })
      .filter(Boolean);
  }, [
    scheduleList,
    getEventIcon,
    selectedEventTypes,
    eventMatchesTypes,
    getCalendarDateInUserTimezone,
  ]);

  const calendarDefaultDate = useMemo(
    () => (selectedDate ? toFloatingDayDate(selectedDate) : new Date()),
    [selectedDate]
  );

  const renderScheduleList = useCallback(
    ({ events: propsEvents } = {}) => {
      // Prioritize events passed from ScheduleCalendar, fallback to internal filtered list
      const hasPropsEvents = Array.isArray(propsEvents) && propsEvents.length > 0;
      const dataToRender = hasPropsEvents
        ? [sortEventsByTypePriority(propsEvents.map((e) => e._raw || e))]
        : filteredEventListonTab?.data;

      const eventCount = hasPropsEvents ? propsEvents.length : filteredEventListonTab?.count;

      return (
        <div className="schedule-list-container-newui">
          {fetchingScheduleList ? (
            <div className="schedule-list-data">
              <div className="filtered_event_count">
                {trans('TOTAL')} ( <LoadingOutlined /> )
              </div>
              <HSCardSkeleton />
            </div>
          ) : eventCount === 0 ? (
            renderNoList()
          ) : (
            dataToRender?.map((list, index) => (
              <div key={'schedule_day_' + index} className="schedule-list-data">
                <div className="filtered_event_count">
                  {`${trans('TOTAL')}${' ( ' + list.length + ' )'}`}
                </div>
                {list.map((item) => (
                  <TaskListItem
                    key={item?.schedule_eventuuid}
                    item={item}
                    onActionClick={onActionClick}
                    renderEventType={renderEventType}
                    renderInvitedList={renderInvitedList}
                    categoryOptions={categoryOptions}
                    loginUserTimeformat={loginUserTimeformat}
                    getRelativeTime={getRelativeTime}
                    EventDaysInfo={EventDaysInfo}
                    DaysOfWeekRow={DaysOfWeekRow}
                    openSubMenu={openSubMenu}
                    hubRecord={hubRecord}
                    renderButtonStatus={renderButtonStatus}
                    userInfo={userInfo}
                    currentUser={currentUser}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      );
    },
    [
      fetchingScheduleList,
      filteredEventListonTab,
      renderNoList,
      trans,
      onActionClick,
      renderEventType,
      renderInvitedList,
      categoryOptions,
      loginUserTimeformat,
      getRelativeTime,
      EventDaysInfo,
      DaysOfWeekRow,
      openSubMenu,
      hubRecord,
      renderButtonStatus,
      userInfo,
      currentUser,
    ]
  );

  const handleCalendarSelectEvent = useCallback(
    (event) => {
      const rawEvent = event._raw ?? event;
      // Determine the correct action based on event type
      let action = Actions.startEvent;
      if (rawEvent?.event_type === 'tasks') {
        action = Actions.viewTask;
      }
      onActionClick(action, rawEvent)();
    },
    [onActionClick]
  );

  const headerSection = useCallback(() => {
    return (
      <div className="page-header-container schedule-header-newui schedule-page-header-container">
        <div className="page-header schedule-title-newui schedule-page-header">
          <span className="schedule-text-newui schedule-page-title-text">
            {openSubMenu ? (
              <div className="hub-schedule-header">
                <div className="hub-icon">
                  <ScheduleListNewIcon className="hub-schedule-icon" />
                </div>
                <div className="hub-schedule-title">{trans('SCHEDULE')}</div>
              </div>
            ) : (
              trans('MYSCHEDULE')
            )}
          </span>
        </div>
      </div>
    );
  }, [openSubMenu, trans, selectedDate, userTimezone]);

  const eventTypeFilterOptions = useMemo(() => {
    const opts = [{ key: 'call', label: trans('CALLS') }];
    if (isCareOrManageRole) opts.push({ key: 'task', label: trans('TASKS') });
    opts.push({ key: 'reminder', label: trans('REMINDER') });
    return opts;
  }, [isCareOrManageRole, trans]);

  const handleEventTypeSelect = (key) => {
    setSelectedEventTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const filterSlotElement = (
    <Dropdown
      trigger={['click']}
      placement="bottomLeft"
      overlayClassName="sc-filter-overlay"
      dropdownRender={() => (
        <div className="sc-filter-menu">
          {eventTypeFilterOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`sc-filter-menu__item${
                selectedEventTypes.includes(opt.key) ? ' is-active' : ''
              }`}
              onClick={() => handleEventTypeSelect(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    >
      <button type="button" className="sc-filter-btn">
        <NewFilterIcon className="filter-icon" />
        <span className="text filter-text">{trans('FILTER_BY_L')}</span>
        {selectedEventTypes.length > 0 && (
          <span className="sc-filter-btn__count">{selectedEventTypes.length}</span>
        )}
      </button>
    </Dropdown>
  );

  return onlyContent ? (
    <div className="list-schedule-container-newui page-content-wrapper only-content">
      {headerSection()}
      <div className={`${!openSubMenu ? 'schedule-content-newui' : ''}`}>
        <div className="schedule-list-container-newui">
          <ScheduleCalendar
            events={transformedEvents}
            defaultView="day"
            record={record}
            openSubMenu={openSubMenu}
            timezone={userTimezone}
            defaultDate={calendarDefaultDate}
            onCalendarChange={onCalendarChange}
            addDropdownProps={scheduleAddDropdownProps}
            onSelectEvent={handleCalendarSelectEvent}
            dayViewComponent={renderScheduleList}
            loading={fetchingScheduleList}
            filterSlot={filterSlotElement}
          />
        </div>
      </div>
    </div>
  ) : (
    <MainPage hasSider={!openSubMenu} header={header} noModals={!!openSubMenu}>
      <div className="list-schedule-container-newui page-content-wrapper with-sider">
        {headerSection()}
        <div className={`${!openSubMenu ? 'schedule-content-newui' : ''}`}>
          <div className="schedule-list-container-newui">
            <ScheduleCalendar
              events={transformedEvents}
              defaultView="day"
              record={record}
              openSubMenu={openSubMenu}
              timezone={userTimezone}
              defaultDate={calendarDefaultDate}
              onCalendarChange={onCalendarChange}
              addDropdownProps={scheduleAddDropdownProps}
              onSelectEvent={handleCalendarSelectEvent}
              dayViewComponent={renderScheduleList}
              loading={fetchingScheduleList}
              filterSlot={filterSlotElement}
            />
          </div>
        </div>
      </div>
    </MainPage>
  );
};

ListSchedule.propTypes = {
  onlyContent: PropTypes.bool,
  parentBread: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object]).isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  scheduleList: PropTypes.array.isRequired,
  fetchingScheduleList: PropTypes.bool.isRequired,
  onActionClick: PropTypes.func.isRequired,
  contacts: PropTypes.array,
  userInfo: PropTypes.object,
  userHubsData: PropTypes.array,
  hubRecord: PropTypes.object,
  openSubMenu: PropTypes.bool,
  loginUserTimeformat: PropTypes.object,
  setParentSelectedTab: PropTypes.func,
  currentUser: PropTypes.object,
  onCalendarChange: PropTypes.func,
};

ListSchedule.defaultProps = {
  onlyContent: false,
  parentBread: [],
  openSubMenu: false,
};

export default ListSchedule;
